import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@weezevent/nacre';

function Step({
    steps,
    form,
    onChangeForm,
    onChangeSteps,
    onClickEdit = () => {},
    child,
    index,
    unbookAttendees = () => {},
    setSelectedZone = () => {},
    setPreviousZone = () => {},
    setBackToSeats = () => {}
}) {
    const { t } = useTranslation();
    const stepIndex = index + 1;
    const title = `${stepIndex} - ${child?.props?.title}`;
    const name = child?.props?.name;
    const nextsubtitle = `${child?.props?.inactive}`;
    const stepState = Object.values(steps)[index];
    const subtitle = stepState !== 'done' ? `${child?.props?.subtitle}` : nextsubtitle;

    const handleSetSelf = React.useCallback(() => {
        onChangeSteps(stepIndex);
        onClickEdit(stepIndex);

        if (['order_step', 'sessions_step', 'inventories_step'].includes(name)) {
            let updated_form = { ...form };
            if (!form.use_inventories) {
                // In any other cases, simply clear attendees
                updated_form.attendees = [];
                onChangeForm({ ...updated_form });
            }
            if (form.use_inventories) {
                if (['sessions_step', 'inventories_step'].includes(name)) {
                    setSelectedZone(null);
                    setPreviousZone(null);
                }
                // To prevent inconstistencies within the datas and avoid errors
                // Attendees must be cleared when returning to order step
                // Thus, Nacre's SelectHierarchy keeping it's own state of the selected rates
                // This selection must be cleared as well
                unbookAttendees(form.attendees, false);
                updated_form.selected_rates = [];
                onChangeForm({ ...updated_form });
            }
        }
    }, [stepIndex, onChangeSteps, form, onChangeForm, setSelectedZone, setPreviousZone, unbookAttendees]);

    return (
        <Card.FormStep
            editLabel={t('ticket.common.action.edit')}
            title={title}
            subtitle={child?.props?.subtitle}
            summary={subtitle}
            onCardEdit={handleSetSelf}
            state={stepState}
            useSteps={true}
        >
            {child}
        </Card.FormStep>
    );
}

export default function Stepper({
    form,
    onChangeForm,
    onChangeSteps,
    onClickEdit,
    step,
    children,
    unbookAttendees,
    bookingSelectionAuto,
    setFullScreenMode,
    setSelectedZone,
    setPreviousZone,
    setBackToSeats
}) {
    const { t } = useTranslation();
    const steps = React.useMemo(() => {
        let output = {};
        children
            .filter(child => child !== undefined)
            .forEach((child, index) => {
                const stepIndex = index + 1;
                step === stepIndex ? (output[index] = 'open') : stepIndex < step ? (output[index] = 'done') : (output[index] = 'closed');
            });

        return output;
    }, [children, step, onChangeSteps]);

    const handleChange = React.useCallback(() => {
        if (Object.values(steps)[step - 2] === 'done' || Object.values(steps)[step - 2] === undefined) {
            onChangeSteps(step);
        } else {
            onChangeSteps(step - 1);
        }
    }, [steps, step, onChangeSteps]);

    React.useEffect(() => {
        // This function ensures that the differents steps will properly displayed on screen
        // preventing the user from scrolling back to top
        const canScroll = step > 1 && !(window.innerWidth <= 800);
        const scroll = canScroll ? 160 * (step - 1) : 0;
        window.scrollTo({ top: scroll });
    }, [step]);

    return (
        <Card.FormManager
            withStepsMobile
            useSteps
            steps={steps}
            onChangeSteps={() => handleChange()}
            labelPreviousStepMobile={t(['ticket.common.back_step'])}
            labelStepMobile={t(['ticket.common.step.current'])}
        >
            {React.Children.toArray(children).map((child, index) => (
                <Step
                    key={index}
                    steps={steps}
                    child={child}
                    index={index}
                    form={form}
                    onChangeForm={onChangeForm}
                    onClickEdit={onClickEdit}
                    onChangeSteps={onChangeSteps}
                    unbookAttendees={unbookAttendees}
                    bookingSelectionAuto={bookingSelectionAuto}
                    setFullScreenMode={setFullScreenMode}
                    setSelectedZone={setSelectedZone}
                    setPreviousZone={setPreviousZone}
                    setBackToSeats={setBackToSeats}
                />
            ))}
        </Card.FormManager>
    );
}
